import React, { useState } from 'react';
import { useEditor } from '../hooks/useEditor';
import { useToast } from '../contexts/ToastContext';
import { useFontManager } from '../hooks/useFontManager';
import { Toolbar } from './Toolbar';
import { ElementLibrary } from './ElementLibrary';
import { Canvas } from './Canvas';
import { PropertiesPanel } from './PropertiesPanel';
import { LayerPanel } from './LayerPanel';
import { ExportModal } from './ExportModal';
import { PreviewModal } from './PreviewModal';
import { FontManager } from './FontManager';
import { Client, BannerSet, BannerSize, ExportOptions } from '../types';
import { exportToJSON, downloadFile, exportBanner } from '../utils/exportUtils';
import { generateThumbnail } from '../utils/thumbnailUtils';
import { ArrowLeft } from 'lucide-react';

interface BannerEditorProps {
  client: Client;
  bannerSet: BannerSet;
  size: BannerSize;
  onUpdateBannerSet: (updates: Partial<BannerSet>) => void;
  onBack: () => void;
}

export const BannerEditor: React.FC<BannerEditorProps> = ({
  client,
  bannerSet,
  size,
  onUpdateBannerSet,
  onBack
}) => {
  const {
    state,
    selectedElement,
    addElement,
    selectElement,
    updateElement,
    deleteElement,
    moveElementLayer,
    setZoom,
    toggleGrid,
    setDataFeed
  } = useEditor(size, bannerSet.dataFeed, bannerSet.dataFeedArray);

  const { customFonts, addCustomFont, removeCustomFont } = useFontManager();
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFontManager, setShowFontManager] = useState(false);
  const { showSuccess, showError, showWarning } = useToast();

  const handleExport = async (options: ExportOptions) => {
    if (state.elements.length === 0) {
      showError('Export Failed', 'Please add some elements to export');
      return;
    }

    try {
      await exportBanner(
        state.elements,
        state.currentSize,
        state.dataFeed,
        state.dataFeedArray,
        options
      );
      showSuccess('Export Complete', 'Your banner has been exported successfully');
    } catch (error) {
      showError('Export Failed', 'An error occurred while exporting your banner');
    }
  };

  const handleSave = async () => {
    if (state.elements.length === 0) {
      showWarning('Nothing to Save', 'Please add some elements before saving');
      return;
    }

    try {
      // Generate thumbnail for the banner
      const thumbnail = await generateThumbnail(
        state.elements,
        state.currentSize,
        state.dataFeed
      );

      // Update the banner set with the current elements and thumbnail
      const updatedSizes = bannerSet.sizes.map(s => 
        s.id === size.id 
          ? { 
              ...s, 
              elements: state.elements, 
              lastModified: new Date(),
              thumbnail
            }
          : s
      );

      onUpdateBannerSet({ 
        sizes: updatedSizes,
        lastModified: new Date()
      });

      showSuccess('Banner Saved', 'Your banner has been saved successfully');
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      
      // Save without thumbnail if generation fails
      const updatedSizes = bannerSet.sizes.map(s => 
        s.id === size.id 
          ? { 
              ...s, 
              elements: state.elements, 
              lastModified: new Date()
            }
          : s
      );

      onUpdateBannerSet({ 
        sizes: updatedSizes,
        lastModified: new Date()
      });

      showSuccess('Banner Saved', 'Your banner has been saved successfully');
    }
  };

  const handleLoad = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const template = JSON.parse(event.target?.result as string);
            // This would load the template elements
            showSuccess('Template Loaded', 'Template has been loaded successfully');
          } catch (error) {
            showError('Load Failed', 'Invalid template file format');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleToggleVisibility = (id: string) => {
    const element = state.elements.find(el => el.id === id);
    if (element) {
      updateElement(id, {
        styles: {
          ...element.styles,
          opacity: element.styles.opacity === 0 ? 1 : 0
        }
      });
    }
  };

  const handleFontAdded = async (font: any) => {
    const success = await addCustomFont(font);
    if (success) {
      showSuccess('Font Added', `${font.name} is now available in the font selector`);
    } else {
      showError('Font Error', 'Failed to load the custom font');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="text-sm">Back to Banner Set</span>
        </button>
      </div>

      <Toolbar
        currentSize={state.currentSize}
        sizes={[size]} // Only show current size
        zoom={state.zoom}
        showGrid={state.showGrid}
        onSizeChange={() => {}} // Disabled in individual editor
        onZoomChange={setZoom}
        onToggleGrid={toggleGrid}
        onExport={() => setShowExportModal(true)}
        onSave={handleSave}
        onLoad={handleLoad}
        onPreview={() => setShowPreviewModal(true)}
        hasDataFeed={state.dataFeedArray.length > 1}
      />

      <div className="flex flex-1 overflow-hidden">
        <ElementLibrary
          onAddElement={addElement}
          dataFeed={state.dataFeed}
          onSetDataFeed={setDataFeed}
        />

        <div className="flex-1 flex flex-col">
          <Canvas
            elements={state.elements}
            size={state.currentSize}
            zoom={state.zoom}
            showGrid={state.showGrid}
            selectedElement={state.selectedElement}
            dataFeed={state.dataFeed}
            onSelectElement={selectElement}
            onUpdateElement={updateElement}
          />

          {/* Full-width bottom panel with layers */}
          <div className="h-48 border-t border-gray-200 bg-white">
            <LayerPanel
              elements={state.elements}
              selectedElement={state.selectedElement}
              onSelectElement={selectElement}
              onMoveLayer={moveElementLayer}
              onToggleVisibility={handleToggleVisibility}
              onUpdateElement={updateElement}
            />
          </div>
        </div>

        <PropertiesPanel
          element={selectedElement}
          dataFeed={state.dataFeed}
          onUpdateElement={updateElement}
          onDeleteElement={deleteElement}
          onOpenFontManager={() => setShowFontManager(true)}
        />
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        elements={state.elements}
        size={state.currentSize}
        dataFeed={state.dataFeed}
        dataFeedArray={state.dataFeedArray}
        onExport={handleExport}
      />

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        elements={state.elements}
        size={state.currentSize}
        dataFeedArray={state.dataFeedArray}
      />

      <FontManager
        isOpen={showFontManager}
        onClose={() => setShowFontManager(false)}
        onFontAdded={handleFontAdded}
        customFonts={customFonts}
        onDeleteFont={removeCustomFont}
      />
    </div>
  );
};